﻿import MediumEditor from 'medium-editor';
import { deepMerge, parseOptions } from '../components/utils';

const mediumSelector = '[data-medium]';

type MediumOptions = Partial<{
  toolbar: {
    allowMultiParagraphSelection?: boolean;
    buttons?: string[];
  };
  anchor: {
    placeholderText?: string;
    targetCheckbox?: boolean;
    targetCheckboxText?: string;
  };
  paste: {
    forcePlainText?: boolean;
    cleanPastedHTML?: boolean;
  };
  extensions: Record<string, any>;
  placeholder: {
    text?: string;
    hideOnClick?: boolean;
  };
}>;

const defaultOptions: MediumOptions = {
  toolbar: {
    allowMultiParagraphSelection: false,
    buttons: ['bold', 'italic', 'underline', 'anchor', 'h2', 'h3'],
  },
  anchor: {
    placeholderText: 'Incolla o digita un link',
    targetCheckbox: true,
    targetCheckboxText: 'Apri in una nuova finestra'
  },
  paste: {
    forcePlainText: true,
    cleanPastedHTML: true,
  },
  extensions: {
    imageDragging: {}
  }
};

export function initMediumEditor(
  element: Element,
  options: MediumOptions = {}
): MediumEditor {

  element._mediumEditorInstance?.destroy();

  // read configuration from data-medium if present using shared parseOptions
  if (element instanceof Element && element.hasAttribute('data-medium')) {
    const raw = (element as HTMLElement).dataset.medium ?? null;
    const parsed = parseOptions<MediumOptions>(raw, {});
    options = deepMerge(options, parsed);
  }

  // ensure placeholder uses element.placeholder when present
  const placeholderText = (element as HTMLInputElement | HTMLTextAreaElement).placeholder ?? '';
  options.placeholder = {
    text: placeholderText,
    hideOnClick: false,
    ...(options.placeholder || {})
  };

  const finalOptions = deepMerge(defaultOptions, options);

  const editor = new MediumEditor(element, finalOptions);

  element._mediumEditorInstance = editor;

  // Subscribe to editor events and sync content back to the original textarea
  try {
    const tryGetEditable = (editableParam?: HTMLElement) => {
      if (editableParam) return editableParam;
      try {
        return (editor as any).elements?.[0] as HTMLElement | undefined;
      } catch {
        return undefined;
      }
    };

    if (element instanceof HTMLTextAreaElement) {
      // initialize editor content from textarea value if possible
      try {
        const initialEditable = tryGetEditable();
        if (initialEditable && element.value) {
          initialEditable.innerHTML = element.value;
        }
      } catch { /* ignore */ }

      // always subscribe; handler will use provided editable or fallback
      editor.subscribe('editableInput', function (event: Event, editable: HTMLElement) {
        try {
          const target = tryGetEditable(editable) as HTMLElement | undefined;
          if (target) {
            element.value = target.innerHTML;
            element.dispatchEvent(new Event('input', { bubbles: true }));
            element.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } catch {  }
      });

      editor.subscribe('blur', function (event: Event, editable: HTMLElement) {
        try {
          const target = tryGetEditable(editable) as HTMLElement | undefined;
          if (target) {
            element.value = target.innerHTML;
            element.dispatchEvent(new Event('input', { bubbles: true }));
            element.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } catch { }
      });
    }

  } catch {
    // swallow any errors related to editor internals
  }

  return editor;
}

export function initMediumEditors(): void {
  const mediumInputs = document.querySelectorAll<Element>(mediumSelector);
  mediumInputs.forEach((element) => {
    initMediumEditor(element);
  });
}

// Initialize on DOMContentLoaded for static pages
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', function () {
    initMediumEditors();
  });
}

